import React, { useState, useEffect } from 'react';
import { getAllStats, getServers } from '../api';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';

export default function ServerRankings() {
  const [serverStats, setServerStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [stats, servers] = await Promise.all([
        getAllStats(),
        getServers().catch(() => []),
      ]);

      // Aggregate blocks by server
      const serverMap = {};
      stats.forEach((s) => {
        const name = s.server_name || 'Desconocido';
        if (!serverMap[name]) {
          serverMap[name] = { name, totalBlocks: 0, playerCount: 0, players: new Set() };
        }
        serverMap[name].totalBlocks += s.blocks_mined || 0;
        serverMap[name].players.add(s.username);
      });

      // Merge server info
      const serverInfo = {};
      servers.forEach((s) => { serverInfo[s.name] = s; });

      const ranked = Object.values(serverMap)
        .map((s) => ({
          ...s,
          playerCount: s.players.size,
          info: serverInfo[s.name] || null,
        }))
        .sort((a, b) => b.totalBlocks - a.totalBlocks);

      setServerStats(ranked);
    } catch (err) {
      setError(err.message || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) return <LoadingSpinner text="Cargando servidores..." />;
  if (error) return <ErrorMessage message={error} onRetry={fetchData} />;

  return (
    <div className="space-y-6">
      <h1 className="mc-title text-mc-diamond text-xl">🖥️ Ranking de Servidores</h1>
      <p className="text-mc-muted text-sm">Servidores ordenados por total de bloques minados</p>

      {serverStats.length === 0 ? (
        <div className="text-center py-12 text-mc-muted">
          <p className="text-4xl mb-4">🖥️</p>
          <p>No hay datos de servidores disponibles</p>
        </div>
      ) : (
        <div className="space-y-4">
          {serverStats.map((server, idx) => (
            <div key={server.name} className={`mc-border rounded-xl p-6 bg-mc-card ${idx === 0 ? 'border-mc-gold/40' : ''}`}>
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-mc-darker">
                  {idx === 0 ? (
                    <span className="text-2xl">🥇</span>
                  ) : idx === 1 ? (
                    <span className="text-2xl">🥈</span>
                  ) : idx === 2 ? (
                    <span className="text-2xl">🥉</span>
                  ) : (
                    <span className="text-mc-muted font-mono text-lg">#{idx + 1}</span>
                  )}
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-mc-text font-bold text-lg">{server.name}</h3>
                  </div>
                  {server.info?.description && (
                    <p className="text-mc-muted text-xs mt-0.5">{server.info.description}</p>
                  )}
                </div>

                <div className="text-right">
                  <p className="text-mc-accent font-bold text-xl">{server.totalBlocks.toLocaleString('es-DO')}</p>
                  <p className="text-mc-muted text-xs">bloques totales</p>
                </div>
              </div>

              <div className="mt-4 flex gap-6 text-sm">
                <div className="flex items-center gap-1">
                  <span className="text-mc-emerald">👥</span>
                  <span className="text-mc-muted">{server.playerCount} jugadores</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-mc-diamond">📊</span>
                  <span className="text-mc-muted">
                    {Math.round(server.totalBlocks / Math.max(server.playerCount, 1)).toLocaleString('es-DO')} bloques/jugador
                  </span>
                </div>
              </div>

              {/* Progress bar */}
              <div className="mt-3 h-2 bg-mc-darker rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-mc-accent to-mc-emerald rounded-full transition-all"
                  style={{ width: `${(server.totalBlocks / Math.max(serverStats[0]?.totalBlocks, 1)) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
